/* eslint-disable no-bitwise */

import isError from 'lodash/isError';
import type { Logger } from '@mimir/common/types/logger';
import { exceptionToString } from '@mimir/common/util/exception';

export type BrowserAACLactencyBehavior =
  'browser-skips-init-samples' |
  'browser-does-not-skip-init-samples' |
  'unknown';

export async function detectBrowserAacLatencyBehavior(logger: Logger): Promise<BrowserAACLactencyBehavior> {
  let videoEl : HTMLVideoElement | null = null;
  let canvasEl : HTMLCanvasElement | null = null;
  try {
    logger.debug('Getting gzippped blob');
    const binaryGzipped = await base64toBlob(AAC_LATENCY_FIX_BASE64_GZIP);
    logger.debug('Deompressing blob');
    const mp4Blob : Blob = await decompressBlob(binaryGzipped);
    logger.debug('Creating video element');
    videoEl = window.document.createElement('video');
    videoEl.width = 8;
    videoEl.height = 4;
    videoEl.src = window.URL.createObjectURL(mp4Blob);

    await new Promise<void>((resolve, reject) => {
      const canPlayTimeout =
        setTimeout(() => { reject('Timed out waiting for canplay event'); }, 5000);

      videoEl!.addEventListener('canplay', () => {
        clearTimeout(canPlayTimeout);
        resolve();
      });
    });

    logger.debug('Seeking to frame 1');

    // Frame 1
    videoEl.currentTime = (1.0 / 50.0);

    await new Promise<void>((resolve, reject) => {
      const seekedTimeout =
        setTimeout(() => { reject('Timed out waiting for seeked event'); }, 5000);
      videoEl!.addEventListener('seeked', () => {
        clearTimeout(seekedTimeout);
        resolve();
      });
    });

    logger.debug('Video seeked. Drawing video on canvas');
    canvasEl = window.document.createElement('canvas');
    canvasEl.width = videoEl.width;
    canvasEl.height = videoEl.height;
    const ctx = canvasEl.getContext('2d');
    if (ctx !== null) {
      ctx.fillStyle = 'red';
      ctx.fillRect(0, 0, videoEl.width, videoEl.height);
      ctx.drawImage(videoEl, 0, 0, videoEl.width, videoEl.height);
      const imageData = ctx.getImageData(0, 0, videoEl.width, videoEl.height);
      const frameNumber = readUnsignedIntegerFromImageDataBitPattern(imageData);
      if (frameNumber !== null) {
        if (frameNumber === 0) {
          logger.info('Detected that the browser does not skip the AAC init samples');
          return 'browser-does-not-skip-init-samples';
        } else if (frameNumber === 1) {
          logger.info('Detected that the browser correctly skips the AAC init samples');
          return 'browser-skips-init-samples';
        } else {
          throw new Error(`Expected either frame number 0 or 1. Got frameNumber=${frameNumber}`);
        }
      } else {
        throw new Error('Failed to determine frame number of video');
      }
    } else {
      throw new Error('Failed to obtain 2d draw context for canvas');
    }
  } catch (error) {
    logger.warn(`Failed to determine AAC latency for browser: ${isError(error) ? error.message : exceptionToString(error)}`);
    return 'unknown';
  } finally {
    videoEl?.remove();
    canvasEl?.remove();
  }
}

function readUnsignedIntegerFromImageDataBitPattern(imageData: ImageData): number | null {
  const bitSize = imageData.height;
  // Assume square bits
  const numberOfBits = imageData.width / bitSize;

  const centerBitOffset = bitSize / 2;

  let value = 0;
  for (let i = 0; i < numberOfBits; i++) {
    const x = (i * imageData.width / numberOfBits) + centerBitOffset;
    const y = centerBitOffset;
    const offset = ((y * imageData.width) + x) * 4;
    const r = imageData.data[offset + 0]!;
    const g = imageData.data[offset + 1]!;
    const b = imageData.data[offset + 2]!;
    if ((r < 10) && (g < 10) && (b < 10)) {
      value = (value << 1) | 0;
    } else if ((r > 240) && (g > 240) && (b > 240)) {
      value = (value << 1) | 1;
    } else {
      return null;
    }
  }
  return value;
}

/**
 *  This is a base64 encoded gzipped MP4 file with h264 video and AAC audio.
 *  The video file has 4 frames with a 2-bit pattern encoded into the image
 *  so that we can read the pixels in order to determine the frame number that
 *  the video element is showing. This base64 can be generated by running
 *  the script: clients/vuetify/scripts/generate_latency_check_video.sh
 */
const AAC_LATENCY_FIX_BASE64_GZIP = `\
  H4sICExGfWYAA2xhdGVuY3lfY2hlY2subXA0AI1WzWskRRSvnsysu0HdiFldJbIlZmUPO5PuziS7
CTYkG4I5RAhIchBxrOmunmmmv9JVk8wsiw6sh1E8iMgieBf9CxRE8LAHl9WzBhZ18QO96E3ES/xV
z4zTmXywTV7Vq/dVr977VWUIIdSV7dgTUUBIjqgZZLId2wjiskEIOe0mnEP3S+Aw+UOOrEM3t1Ca
vVIydJ3k9KVHodw+VdjfD+6t/vHlb3trn926/D3de+7Pv1rmfJkWqR0lnBpgk1l9YY5WGedlXYdi
rQSDmZc2Vl8sluny1gosHW5DsRLFbZ+7kpq6Pls0ddOEsC5lvDgzs7u7W9rxHB75LCxFSW1G7VKq
y8CHTRRLLwrFIrVZldmWThPuWrPU4VU/shuWsagv6pSFzG8LbuktrFuGYdCAW3XeoqJZBXeFxqJt
GWqsJI5llHQ4YaCB1+JORUVUHpWEhTVuGfPUridRwCpwNahMuO97AtzV1lXHlkjB3g4wOpw516OQ
W6ZxGTu6TMhKLBpeDNN+gO24Ermu4NIqmlTWE3ioQH4UNVgdi8pQJnzP5kOBTsMk3cP2AiZVHl4o
eeIzGEFe9ZsJa1fsKIhZmhFKJBPmhQgBw4QpGzdhAVehdrlXq8sYXIO3obbMuQFbCbwQSypsHnK7
Ka2yTtMAqioJF3VVcbvyf8ZKn9iWnbg0qKI0KjMsLHO2pNNtlY+ll+bBxipwOrOWNb8ARkgeW2Xq
xagzeoo2IBbbRgtVQ0gHyBzn3bcu3HmH5Br/aOfpQ5CcXe52rTudnwjNaddeW+8Mxe/1xETLnfl4
qS8eX+5++Obzl8iZj17vSQofBFG0A40f7NQdcuAb+x3DFtGI+ht+2kGr0fUSOfEbw9XxUL4G+Fdl
I91z7IhoG4c8H2Tf06B8upzmjhSYp7gvZMZjY+ir3Q8cj4GhgTN6djMNtHk7XRTrjp8MNOoiZi23
1MVcY6Hjc2WjvYu+umAmd4I0aDbNaaenm3IAnkxSDzcTn/Z4bV3Iqg/+cyGFk7H5RD1Qx5RCHTxP
1jCvDSzOHXi0qO9V1aOR8Ti/v4+xBKsV7dpX4/v3yRM1zHsXbt3IL6meYHhq7Pbb72ukUL97867K
byJmIu7vr2gSCO8VVzt7IyXEFTIt/CDPfD/dSZwnK1c0BZk9IhvQNHTXM/kiTu4m5sf7hDeYUCHt
aKgnq7D5BvQz6F/Q5glAy/XnrYxM046q7skAPw5om8Oja98eD7QvOijNkUATUTPMWr6MtZMBWqsP
tAkRHAj6ICB7oQ+yN0ZAFuI/IDt07ByZGKSKb54LJ23kWKfTuQhlHvOTS+dSn0e6eGS+K0BSeKy7
9Ss5BU5Tpc5ApWdDDkOl0L+8J8Hi0ggs4EPU1vkRms5AQ9noOMbXoB9BfyN39VI8LWqxo4Imke+T
HiS09FpMiWpNAX2g6WeXztWmI1WNXgm4HNTq2Wzj0OyExbGfbV7R66Hj4qcyUmk9g18WbFBkXFUX
V9VMf1/8B9IGYkOgCAAA
`;

async function base64toBlob(base64: string) {
  const response = await fetch(`data:application/octet-stream;base64,${base64}`);
  return response.blob();
}

async function decompressBlob(blob: Blob): Promise<Blob> {
  const ds = new DecompressionStream('gzip');
  const decompressedStream = blob.stream().pipeThrough(ds);
  return await new Response(decompressedStream).blob();
}
